import { eventBus } from "../../src/helpers/eventBus.helper";
import { toast } from "sonner";

/**
 * Generic interface for HTTP clients
 */
interface HttpResponse<T> {
  data: T;
  status?: number;
  headers?: any;
}

interface HttpClient {
  interceptors: {
    request: any;
    response: {
      use: (
        onFulfilled?: (response: any) => any,
        onRejected?: (error: any) => any
      ) => void;
    };
  };
  get: (url: string, options?: any) => Promise<HttpResponse<any>>;
  post: (url: string, data?: any, options?: any) => Promise<HttpResponse<any>>;
  put: (url: string, data?: any, options?: any) => Promise<HttpResponse<any>>;
  patch: (url: string, data?: any, options?: any) => Promise<HttpResponse<any>>;
  delete: (url: string, options?: any) => Promise<HttpResponse<any>>;
}

/**
 * RootAPI class to abstract and adapt the use of fetch libraries.
 */
class RootAPI {
  #clientFetcher: HttpClient;

  /**
   * Create an instance of RootAPI.
   * @param {HttpClient} fetcher - The fetch client instance (e.g., axios, fetch, etc.).
   */
  constructor(fetcher: HttpClient) {
    this.#clientFetcher = fetcher;
    this.enableInterceptors();
  }

  /**
   * Enable interceptors for the fetch client.
   * Interceptors include handling error responses.
   */
  private enableInterceptors(): void {
    this.#clientFetcher.interceptors.response.use(
      (response) => {
        return Promise.resolve(response);
      },
      (error) => {
        console.error("RootAPI error:", error);
        if (error.response && error.response.status === 401) {
          // During grace period (e.g. org encryption setup countdown),
          // suppress both the toast and the logout event
          if (!eventBus.isInGracePeriod()) {
            toast.error("Sua conexão expirou. Por favor, entre novamente.");
          }
          eventBus.emit("UNAUTHORIZED_ERROR");
        }

        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, options = {}): Promise<HttpResponse<T>> {
    return this.#clientFetcher.get(url, options);
  }

  async post<T>(url: string, data: any, options = {}): Promise<HttpResponse<T>> {
    return this.#clientFetcher.post(url, data, options);
  }

  async put<T>(url: string, data: any, options = {}): Promise<HttpResponse<T>> {
    return this.#clientFetcher.put(url, data, options);
  }

  async patch<T>(url: string, data: any, options = {}): Promise<HttpResponse<T>> {
    return this.#clientFetcher.patch(url, data, options);
  }

  async delete<T>(url: string, options = {}): Promise<HttpResponse<T>> {
    return this.#clientFetcher.delete(url, options);
  }
}

export default RootAPI;
